/**
 * Header search shell: input stays in the header; overlay + popup open below it.
 */
class SearchModal extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.js-section-header');
    this.overlay = this.querySelector('[data-search-overlay]');
    this.input = this.querySelector('.header-search__input');
    this.field = this.querySelector('.header-search__field');
    this.closeButtons = this.querySelectorAll('.search-modal__close-button');

    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.onKeyupEvent = this.onKeyup.bind(this);
    this.onResizeEvent = this.syncPopupGeometry.bind(this);
    this.onViewportEvent = this.syncPopupGeometry.bind(this);

    this.input?.addEventListener('focus', () => this.open());
    this.field?.addEventListener('mousedown', (event) => {
      if (event.target.closest('button[type="submit"], button[type="reset"]')) return;
      this.open();
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close(true);
      });
    });

    this.overlay?.addEventListener('click', () => this.close(false));
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Keep the popup below the search field and above the iOS keyboard
   * (visualViewport shrinks when the keyboard is open).
   */
  syncPopupGeometry() {
    const anchor = this.field || this.input;
    if (anchor) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const gap = isMobile ? 0 : 4;
      const popupTop = Math.round(anchor.getBoundingClientRect().bottom + gap);
      document.documentElement.style.setProperty('--search-popup-top', `${popupTop}px`);
    }

    const vv = window.visualViewport;
    if (!vv) {
      document.documentElement.style.setProperty('--search-popup-keyboard-inset', '0px');
      return;
    }
    const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    document.documentElement.style.setProperty('--search-popup-keyboard-inset', `${inset}px`);
  }

  positionPopup() {
    this.syncPopupGeometry();
  }

  mountOverlay() {
    if (!this.overlay) return;
    if (this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }
    this.overlay.classList.add('is-open');
  }

  unmountOverlay() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-open');
    if (this.overlay.parentElement === document.body) {
      this.insertBefore(this.overlay, this.firstChild);
    }
  }

  disconnectedCallback() {
    if (this.isOpen()) {
      this.close(false);
      return;
    }
    // Overlay may still be on body if mount ran without a clean close.
    this.unmountOverlay();
  }

  // Block AT/keyboard on non-search header siblings (incl. header-drawer).
  // Visual blur is a CSS ::after backdrop — do not rely on filter on these nodes.
  setHeaderSiblingsInert(inert) {
    const headerEl = this.closest('.header');
    if (!headerEl) return;

    [...headerEl.children].forEach((el) => {
      if (el.classList.contains('header__search')) return;
      if (inert) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  _bindViewportListeners() {
    window.addEventListener('resize', this.onResizeEvent);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportEvent);
      window.visualViewport.addEventListener('scroll', this.onViewportEvent);
    }
  }

  _unbindViewportListeners() {
    window.removeEventListener('resize', this.onResizeEvent);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportEvent);
      window.visualViewport.removeEventListener('scroll', this.onViewportEvent);
    }
  }

  open() {
    const wasOpen = this.isOpen();
    this.setAttribute('open', '');
    this.mountOverlay();
    this.syncPopupGeometry();

    if (this.header) {
      this.header.classList.add('search-open');
    }
    document.body.classList.add('overflow-hidden', 'search-popup-open');

    // Fallback search-form path has no predictive host to manage is_fixed.
    const hasPredictiveHost = Boolean(this.querySelector('predictive-search, constructor-autocomplete'));
    if (!hasPredictiveHost) {
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.classList.add('is_fixed');
      document.body.style.top = `-${this._savedScrollY}px`;
    }

    this.setHeaderSiblingsInert(true);

    if (!wasOpen) {
      if (!window.Shopify?.designMode) {
        requestAnimationFrame(() => {
          document.body.addEventListener('click', this.onBodyClickEvent);
        });
      }
      document.addEventListener('keyup', this.onKeyupEvent);
      this._bindViewportListeners();

      if (window.FocusLayer) {
        window.FocusLayer.push(this, {
          contain: true,
          modal: false,
          lockScroll: false,
          initialFocus: this.input,
          onDismiss: () => this.close(true),
        });
      }
    }

    const host = this.querySelector('predictive-search, constructor-autocomplete');
    const query = host?.getQuery?.() || this.input?.value?.trim() || '';
    if (host && !query && typeof host.showIdle === 'function') {
      host.showIdle();
    }
  }

  close(blurInput = false) {
    if (!this.isOpen()) return;

    this.removeAttribute('open');
    this.unmountOverlay();

    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.removeEventListener('keyup', this.onKeyupEvent);
    this._unbindViewportListeners();
    document.body.classList.remove('overflow-hidden', 'is_fixed', 'search-popup-open');
    document.documentElement.style.removeProperty('--search-popup-top');
    document.documentElement.style.removeProperty('--search-popup-keyboard-inset');

    if (typeof this._savedScrollY === 'number') {
      document.body.style.top = '';
      window.scrollTo(0, this._savedScrollY);
      this._savedScrollY = null;
    }

    this.setHeaderSiblingsInert(false);

    try {
      if (window.FocusLayer) {
        // Don't return focus to the input — its focus handler would reopen the popup.
        const returnFocus = blurInput
          ? this.querySelector('button[type="submit"]') || document.body
          : document.body;
        window.FocusLayer.pop(this, { returnFocus });
      }
    } finally {
      if (this.header) {
        this.header.classList.remove('search-open');
      }

      const host = this.querySelector('predictive-search, constructor-autocomplete');
      if (host && typeof host.closeResults === 'function') {
        host.closeResults(false);
      }

      if (blurInput && this.input) {
        this.input.blur();
      }
    }
  }

  onBodyClick(event) {
    if (window.Shopify?.designMode) return;
    if (this.contains(event.target)) return;
    if (event.target === this.overlay || this.overlay?.contains(event.target)) {
      this.close(false);
      return;
    }
    if (!event.target.closest?.('search-modal, .search-popup-panels')) {
      this.close(false);
    }
  }

  onKeyup(event) {
    if (event.code === 'Escape') this.close(true);
  }
}

customElements.define('search-modal', SearchModal);
